import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Warehouse, Plus, LogOut, Trash2, Package, Scale, RefreshCw, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const Cella = () => {
  const [bins, setBins] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUscitaModal, setShowUscitaModal] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  const [stats, setStats] = useState({ bins_attivi: 0, kg_totale: 0 });

  const [formData, setFormData] = useState({
    prodotto_sigla: '',
    fornitore: '',
    kg: '',
    data_ingresso: getItalianDateTime(),
    note: ''
  });

  const [uscitaData, setUscitaData] = useState({
    kg_uscita: '',
    data_uscita: getItalianDateTime(),
    note: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [binsRes, fornitoriRes, prodottiRes, statsRes] = await Promise.all([
        axios.get(`${API}/cella/bins`).catch(e => ({ data: [] })),
        axios.get(`${API}/fornitori`).catch(e => ({ data: [] })),
        axios.get(`${API}/prodotti`).catch(e => ({ data: [] })),
        axios.get(`${API}/cella/stats`).catch(e => ({ data: { bins_attivi: 0, kg_totale: 0, bins: [] } }))
      ]);
      setBins((binsRes.data || []).filter(b => b.status === 'in_cella'));
      setFornitori(fornitoriRes.data || []);
      setProdotti(prodottiRes.data || []);
      setStats(statsRes.data || { bins_attivi: 0, kg_totale: 0, bins: [] });
    } catch (error) {
      console.error('Errore caricamento dati cella:', error);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddBin = async (e) => {
    e.preventDefault();
    const prodotto = prodotti.find(p => p.sigla === formData.prodotto_sigla);
    
    try {
      await axios.post(`${API}/cella/bins`, {
        prodotto_sigla: formData.prodotto_sigla,
        prodotto_nome: prodotto?.nome || formData.prodotto_sigla,
        fornitore: formData.fornitore,
        kg: parseFloat(formData.kg),
        data_ingresso: new Date(formData.data_ingresso).toISOString(),
        note: formData.note
      });
      toast.success('Prodotto aggiunto in cella');
      setShowAddModal(false);
      setFormData({
        prodotto_sigla: '',
        fornitore: '',
        kg: '',
        data_ingresso: getItalianDateTime(),
        note: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta prodotto');
    }
  };

  const handleUscita = async (e) => {
    e.preventDefault();
    if (!selectedBin) return;
    
    try {
      await axios.post(`${API}/cella/uscita`, {
        prodotto_sigla: selectedBin.prodotto_sigla,
        kg_uscita: parseFloat(uscitaData.kg_uscita),
        data_uscita: new Date(uscitaData.data_uscita).toISOString(),
        note: uscitaData.note
      });
      toast.success('Uscita registrata');
      setShowUscitaModal(false);
      setSelectedBin(null);
      setUscitaData({
        kg_uscita: '',
        data_uscita: getItalianDateTime(),
        note: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore uscita');
    }
  };

  const handleDelete = async (binId) => {
    if (!window.confirm('Eliminare questo prodotto dalla cella?')) return;
    
    try {
      await axios.delete(`${API}/cella/bins/${binId}`);
      toast.success('Prodotto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const openUscitaModal = (bin) => {
    setSelectedBin(bin);
    setUscitaData({
      kg_uscita: bin.kg_attuali.toString(),
      data_uscita: getItalianDateTime(),
      note: ''
    });
    setShowUscitaModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#8B5CF6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cella-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#8B5CF6]/20 rounded-xl">
            <Warehouse className="w-6 h-6 text-[#8B5CF6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white uppercase tracking-tight">Cella</h1>
            <p className="text-sm text-white/50">Prodotti senza depurazione</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            title="Aggiorna"
          >
            <RefreshCw className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#8B5CF6] text-white rounded-lg font-medium hover:bg-[#8B5CF6]/80 transition-colors"
            data-testid="add-cella-btn"
          >
            <Plus className="w-5 h-5" />
            Aggiungi
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-[#8B5CF6]" />
            <div>
              <p className="text-sm text-white/50">Prodotti Attivi</p>
              <p className="text-2xl font-bold text-white">{stats.bins_attivi}</p>
            </div>
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <Scale className="w-8 h-8 text-[#22C55E]" />
            <div>
              <p className="text-sm text-white/50">KG Totali</p>
              <p className="text-2xl font-bold text-[#22C55E]">{stats.kg_totale.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista Prodotti */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Prodotto</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-[#FFE500] uppercase">Fornitore</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-[#FFE500] uppercase">KG</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-[#FFE500] uppercase">Data Ingresso</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-[#FFE500] uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {bins.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-white/50">
                    Nessun prodotto in cella
                  </td>
                </tr>
              ) : (
                bins.map((bin) => (
                  <tr key={bin.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-sm">
                          {bin.prodotto_sigla}
                        </span>
                        <span className="text-white/70 text-sm">{bin.prodotto_nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70">{bin.fornitore}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-[#22C55E]">{bin.kg_attuali} kg</span>
                    </td>
                    <td className="px-4 py-3 text-center text-white/70">
                      {format(new Date(bin.data_ingresso), 'dd/MM/yy HH:mm')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openUscitaModal(bin)}
                          className="p-2 bg-[#EF4444]/20 hover:bg-[#EF4444]/30 rounded-lg transition-colors"
                          title="Registra Uscita"
                          data-testid={`uscita-btn-${bin.id}`}
                        >
                          <LogOut className="w-4 h-4 text-[#EF4444]" />
                        </button>
                        <button
                          onClick={() => handleDelete(bin.id)}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Aggiungi */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F172A] border border-white/20 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#8B5CF6]" />
              Aggiungi in Cella
            </h3>
            
            <form onSubmit={handleAddBin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Prodotto *</label>
                <select
                  value={formData.prodotto_sigla}
                  onChange={(e) => setFormData({ ...formData, prodotto_sigla: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                  data-testid="cella-prodotto-select"
                >
                  <option value="" className="bg-[#0F172A]">Seleziona prodotto...</option>
                  {prodotti.map((p) => (
                    <option key={p.sigla} value={p.sigla} className="bg-[#0F172A]">
                      {p.sigla} - {p.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Fornitore *</label>
                <select
                  value={formData.fornitore}
                  onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                  data-testid="cella-fornitore-select"
                >
                  <option value="" className="bg-[#0F172A]">Seleziona fornitore...</option>
                  {fornitori.map((f) => (
                    <option key={f.nome} value={f.nome} className="bg-[#0F172A]">{f.nome}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">KG *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.kg}
                  onChange={(e) => setFormData({ ...formData, kg: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                  data-testid="cella-kg-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Data Ingresso</label>
                <input
                  type="datetime-local"
                  value={formData.data_ingresso}
                  onChange={(e) => setFormData({ ...formData, data_ingresso: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#8B5CF6] text-white rounded-lg font-medium hover:bg-[#8B5CF6]/80"
                  data-testid="cella-submit-btn"
                >
                  Aggiungi
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Uscita */}
      {showUscitaModal && selectedBin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F172A] border border-white/20 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <LogOut className="w-5 h-5 text-[#EF4444]" />
              Uscita da Cella
            </h3>
            
            <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-1 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-sm">
                  {selectedBin.prodotto_sigla}
                </span>
                <span className="text-white font-medium">{selectedBin.prodotto_nome}</span>
              </div>
              <p className="text-sm text-white/70">{selectedBin.fornitore} - {selectedBin.kg_attuali} kg disponibili</p>
            </div>
            
            <form onSubmit={handleUscita} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">KG Uscita</label>
                <input
                  type="number"
                  step="0.01"
                  max={selectedBin.kg_attuali}
                  value={uscitaData.kg_uscita}
                  onChange={(e) => setUscitaData({ ...uscitaData, kg_uscita: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                  data-testid="cella-uscita-kg-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Data Uscita</label>
                <input
                  type="datetime-local"
                  value={uscitaData.data_uscita}
                  onChange={(e) => setUscitaData({ ...uscitaData, data_uscita: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Note (opzionale)</label>
                <textarea
                  value={uscitaData.note}
                  onChange={(e) => setUscitaData({ ...uscitaData, note: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUscitaModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#EF4444] text-white rounded-lg font-medium hover:bg-[#EF4444]/80"
                  data-testid="cella-uscita-submit-btn"
                >
                  Registra Uscita
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cella;
